// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 2dc1d33e-8bb8-47d9-9bab-73ba8238c540

// ---------------------------------------------------------------------------
// Primordia — Relation Editor
//
// The last missing piece of the LeanIX meta-model: a relation is not just a
// link, it is a RECORD that carries its own fields.
//
// The cost of an IT Component *as used by this one application*, the risk that
// this specific dependency introduces, how essential the link is, when it is
// valid from/to — none of those values belong on either fact sheet. They exist
// only because the two are connected, so they live on the edge.
//
// In PostgreSQL that is `relation.attributes jsonb` (backend/sql/001_schema.sql);
// in the browser store it is:
//
//     factSheet.relations[relKey] = [{ id, attributes: { cost, risk, ... } }]
//
// The attributes themselves are DATA (RELATION_ATTRIBUTES below), not JSX — add
// a row to the array and it renders, edits, saves and summarises itself, the
// same schema-as-data spirit as schema.js.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Collapse, Divider, IconButton, MenuItem,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import TuneIcon from '@mui/icons-material/Tune';
import { FIELD_TYPES, RELATION_TYPES, relationMeta, schemaFor, typeMeta } from './schema';
import { TypeBadge } from './ui';
import store from './store';

// Optimum palette
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

// ---------------------------------------------------------------------------
// The fields a RELATION carries. Schema-as-data: add an entry and the editor,
// the collapsed summary and the save payload all pick it up with no code change.
//   type     — reuses FIELD_TYPES so it renders like any other Primordia field
//   summary  — optional: how the value appears on the collapsed row
//   colors   — optional: chip colour per value (select fields)
// ---------------------------------------------------------------------------
export const RELATION_ATTRIBUTES = [
  {
    key: 'cost',
    label: 'Cost for this connection (annual)',
    type: FIELD_TYPES.CURRENCY,
    help: 'What this dependency costs this fact sheet — not the vendor list price.',
    // Same as the fact-sheet renderer: a non-numeric cost is shown as captured
    // rather than as "$NaN/yr".
    summary: (v) => (Number.isFinite(Number(v)) ? `$${Number(v).toLocaleString()}/yr` : `${v}/yr`),
  },
  {
    key: 'risk',
    label: 'Risk',
    type: FIELD_TYPES.SELECT,
    options: ['Low', 'Medium', 'High', 'Critical'],
    help: 'Operational risk this link introduces here.',
    summary: (v) => `risk: ${v}`,
    colors: { Low: '#2E7D32', Medium: '#F5A623', High: '#EF6C00', Critical: '#D0021B' },
  },
  {
    key: 'criticality',
    label: 'Criticality of the dependency',
    type: FIELD_TYPES.SELECT,
    options: ['Nice to have', 'Important', 'Essential'],
    help: 'How badly this fact sheet needs the connection.',
    summary: (v) => v,
    colors: { 'Nice to have': GREY, Important: BLUE, Essential: HEADER },
  },
  { key: 'validFrom', label: 'Valid from', type: FIELD_TYPES.DATE, summary: (v) => `from ${v}` },
  { key: 'validTo', label: 'Valid to', type: FIELD_TYPES.DATE, summary: (v) => `to ${v}` },
  { key: 'notes', label: 'Notes', type: FIELD_TYPES.TEXTAREA, full: true },
];

// Relation keys that are meaningful FROM a fact-sheet type that has no
// relations declared on its (lightweight) schema. Kept local and declarative so
// schema.js stays untouched; drop entries here as those schemas grow.
const EXTRA_RELATION_SOURCES = {
  ITComponent: ['itComponents', 'providers', 'contracts', 'platforms', 'initiatives'],
  Interface: ['dataObjects', 'itComponents', 'providers'],
  Platform: ['businessCapabilities', 'organizations', 'itComponents', 'initiatives'],
  Provider: ['contracts', 'itComponents'],
  Contract: ['providers'],
  Initiative: ['businessCapabilities', 'organizations', 'objectives'],
  // BusinessCapability is deliberately absent here now: schema.js declares its
  // relations directly (hierarchy + businessSupport sections), which wins
  // before this fallback map is ever consulted — see relationsForType() below.
  BusinessContext: ['businessCapabilities', 'organizations'],
  Organization: ['businessCapabilities', 'organizations'],
  DataObject: ['dataObjects', 'organizations'],
  TechCategory: ['itComponents'],
  Objective: ['initiatives', 'organizations'],
};

const isEmpty = (v) => v === undefined || v === null || v === '';

// Which relation types can start FROM this fact-sheet type.
//   1. an explicit `source` on the relation def (string or array) wins,
//   2. otherwise the relations declared by the type's own schema sections,
//   3. otherwise the local map above.
function relationsForType(type) {
  const explicit = RELATION_TYPES.filter((r) => {
    if (!r.source) return false;
    return Array.isArray(r.source) ? r.source.includes(type) : r.source === type;
  });
  if (explicit.length) return explicit;

  const declared = [];
  (schemaFor(type).sections || []).forEach((s) =>
    (s.relations || []).forEach((k) => { if (!declared.includes(k)) declared.push(k); })
  );
  const keys = declared.length ? declared : (EXTRA_RELATION_SOURCES[type] || []);
  return keys.map((k) => relationMeta(k)).filter(Boolean);
}

// Strip empty values so we never persist `{ cost: '', notes: '' }`.
function cleanAttributes(attrs = {}) {
  const out = {};
  RELATION_ATTRIBUTES.forEach((a) => {
    const v = attrs[a.key];
    if (!isEmpty(v)) out[a.key] = v;
  });
  return out;
}

// One attribute control, driven by the attribute's declared type.
function AttributeEditor({ attr, value, onChange }) {
  const common = {
    size: 'small',
    fullWidth: true,
    label: attr.label,
    value: value ?? '',
    helperText: attr.help,
    onChange: (e) => onChange(e.target.value),
  };
  switch (attr.type) {
    case FIELD_TYPES.SELECT:
      return (
        <TextField {...common} select>
          <MenuItem value=""><em>—</em></MenuItem>
          {(attr.options || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case FIELD_TYPES.DATE:
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
    case FIELD_TYPES.CURRENCY:
    case FIELD_TYPES.NUMBER:
      return (
        <TextField
          {...common}
          type="number"
          InputProps={{ startAdornment: attr.type === FIELD_TYPES.CURRENCY
            ? <Box component="span" sx={{ mr: 0.5, color: GREY }}>$</Box> : undefined }}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case FIELD_TYPES.TEXTAREA:
      return <TextField {...common} multiline minRows={2} />;
    default:
      return <TextField {...common} />;
  }
}

// Compact "$12,000/yr · risk: High" strip shown without expanding.
function SummaryChips({ attributes = {} }) {
  const items = RELATION_ATTRIBUTES.filter((a) => a.summary && !isEmpty(attributes[a.key]));
  if (!items.length) {
    return <Typography sx={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>no attributes yet</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
      {items.map((a) => {
        const v = attributes[a.key];
        const color = (a.colors || {})[v];
        return (
          <Chip
            key={a.key}
            size="small"
            label={a.summary(v)}
            sx={{
              height: 20, fontSize: 11, fontWeight: 600,
              color: color || GREY,
              border: `1px solid ${color || '#d5dae0'}`,
              bgcolor: color ? `${color}12` : 'transparent',
            }}
          />
        );
      })}
    </Stack>
  );
}

export default function RelationEditor({ factSheet, onSaved }) {
  // rels: { [relKey]: [{ id, name, type, attributes, rest }] }
  const [rels, setRels] = useState({});
  const [options, setOptions] = useState({});   // targetType -> [factSheet]
  const [expanded, setExpanded] = useState(null); // `${relKey}:${id}`
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const fsType = factSheet?.type;
  const fsId = factSheet?.id;
  const tenantId = factSheet?.tenantId;

  const relDefs = useMemo(() => (fsType ? relationsForType(fsType) : []), [fsType]);
  // Precomputed once per (options, rels) rather than inside the render map,
  // where it re-ran for every relation def on every render — and `rows.some`
  // made each one O(options x rows): ~50k comparisons per render at 4,000
  // applications, on every expand and every attribute keystroke.
  const choicesByDef = useMemo(() => {
    const out = {};
    relDefs.forEach((def) => {
      const taken = new Set((rels[def.key] || []).map((r) => r.id));
      out[def.key] = (options[def.target] || []).filter((c) => c.id !== fsId && !taken.has(c.id));
    });
    return out;
  }, [relDefs, rels, options, fsId]);
  const targetTypes = useMemo(
    () => [...new Set(relDefs.map((r) => r.target))],
    [relDefs]
  );

  // Seed local state from the resolved fact sheet.
  useEffect(() => {
    if (!factSheet) return;
    const raw = factSheet.relations || {};
    const resolved = factSheet.resolvedRelations || {};
    const next = {};
    relDefs.forEach((def) => {
      const entries = raw[def.key] || [];
      next[def.key] = entries.map((entry) => {
        const { id, attributes, ...rest } = entry || {};
        const hit = (resolved[def.key] || []).find((r) => r.id === id);
        return {
          id,
          // A target that no longer resolves used to render as its raw uuid,
          // which reads like data corruption. Say what actually happened.
          name: hit?.name || rest.name || 'Deleted or unavailable fact sheet',
          missing: !hit && !rest.name,
          type: hit?.type || def.target,
          attributes: { ...(attributes || {}) },
          rest,
        };
      }).filter((r) => r.id);
    });
    setRels(next);
    setDirty(false);
    setExpanded(null);
  }, [factSheet, relDefs]);

  // Candidate targets for every relation target type, within this tenant.
  useEffect(() => {
    if (!tenantId || !targetTypes.length) return;
    let alive = true;
    Promise.all(targetTypes.map((t) => store.listFactSheets(tenantId, { type: t }))).then((lists) => {
      if (!alive) return;
      const map = {};
      targetTypes.forEach((t, i) => { map[t] = lists[i] || []; });
      setOptions(map);
    });
    return () => { alive = false; };
  }, [tenantId, targetTypes]);

  if (!factSheet) return null;

  const mutate = (relKey, fn) => {
    setRels((prev) => ({ ...prev, [relKey]: fn(prev[relKey] || []) }));
    setDirty(true);
  };

  const addTarget = (def, target) => {
    if (!target) return;
    const row = { id: target.id, name: target.name, type: target.type, attributes: {}, rest: {} };
    mutate(def.key, (rows) => {
      if (def.single) return [row];                       // single: replace, don't append
      if (rows.some((r) => r.id === target.id)) return rows; // already linked
      return [...rows, row];
    });
    setExpanded(`${def.key}:${target.id}`);
  };

  const removeTarget = (def, row) => {
    const what = def.single ? def.label : `${row.name} from ${def.label}`;
    if (!window.confirm(`Remove ${what}? The attributes captured on this connection are lost.`)) return;
    mutate(def.key, (rows) => rows.filter((r) => r.id !== row.id));
  };

  const setAttribute = (relKey, id, attrKey, value) =>
    mutate(relKey, (rows) =>
      rows.map((r) => (r.id === id ? { ...r, attributes: { ...r.attributes, [attrKey]: value } } : r))
    );

  const save = async () => {
    setSaving(true);
    // Preserve everything else on the fact sheet, and every relation key we do
    // not manage here.
    const relations = { ...(factSheet.relations || {}) };
    Object.keys(rels).forEach((relKey) => {
      const rows = rels[relKey] || [];
      if (!rows.length) { delete relations[relKey]; return; }
      relations[relKey] = rows.map((r) => ({ ...r.rest, id: r.id, attributes: cleanAttributes(r.attributes) }));
    });
    try {
      await store.saveFactSheet({ ...factSheet, relations });
      setDirty(false);
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const totalLinks = Object.values(rels).reduce((n, rows) => n + (rows ? rows.length : 0), 0);


  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#dfe4ea' }}>
      {/* Header + the point of the feature, said plainly. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <TuneIcon sx={{ color: BLUE, fontSize: 20 }} />
        <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 15 }}>
          Relations &amp; their attributes
        </Typography>
        <Chip size="small" label={`${totalLinks} link${totalLinks === 1 ? '' : 's'}`}
          sx={{ height: 20, fontSize: 11, bgcolor: 'chipMuted.main', color: HEADER, fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        {dirty && <Typography sx={{ fontSize: 12, color: '#EF6C00', fontWeight: 600 }}>Unsaved changes</Typography>}
        <Button size="small" variant="contained" startIcon={<SaveIcon />} disabled={!dirty || saving}
          onClick={save} sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}>
          {saving ? 'Saving…' : 'Save relations'}
        </Button>
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: GREY, mb: 2, maxWidth: 900 }}>
        These values describe the <strong>connection itself</strong>, not either end of it. The cost, risk and
        criticality below belong to <em>{factSheet.name}</em>&rsquo;s use of the linked fact sheet — the same
        component can be cheap and low risk for one application and business critical for another. Nothing here
        changes the fact sheet on the other side.
      </Typography>

      {!relDefs.length && (
        <Typography sx={{ fontSize: 13, color: GREY }}>
          No relation types are defined for {typeMeta(fsType).label} fact sheets yet.
        </Typography>
      )}

      {relDefs.map((def, defIdx) => {
        const rows = rels[def.key] || [];
        const choices = choicesByDef[def.key] || [];
        return (
          <Box key={def.key} sx={{ mb: 2.5 }}>
            {defIdx > 0 && <Divider sx={{ mb: 2.5 }} />}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TypeBadge type={def.target} size={18} />
              <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13.5 }}>{def.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: GREY }}>
                {def.section} · {typeMeta(def.target).label}{def.single ? ' · single' : ''}
              </Typography>
            </Stack>

            {rows.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: '#9aa5b1', fontStyle: 'italic', mb: 1 }}>
                No {typeMeta(def.target).label.toLowerCase()} linked yet — add one below, then expand it to record
                what the connection costs and how risky it is.
              </Typography>
            ) : (
              rows.map((row) => {
                const rowKey = `${def.key}:${row.id}`;
                const open = expanded === rowKey;
                return (
                  <Paper key={rowKey} variant="outlined"
                    sx={{ mb: 1, borderColor: open ? BLUE : 'divider', bgcolor: open ? 'action.selected' : 'background.paper' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.75 }}>
                      <IconButton size="small" onClick={() => setExpanded(open ? null : rowKey)}
                        aria-label={open ? 'Collapse attributes' : 'Edit attributes of this connection'}>
                        <ExpandMoreIcon fontSize="small"
                          sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', color: GREY }} />
                      </IconButton>
                      <TypeBadge type={row.type} size={16} />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: HEADER, minWidth: 180 }}>
                        {row.name}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <SummaryChips attributes={row.attributes} />
                      </Box>
                      <Tooltip title="Remove this relation">
                        <IconButton size="small" onClick={() => removeTarget(def, row)}>
                          <DeleteOutlineIcon fontSize="small" sx={{ color: '#b34747' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Collapse in={open} unmountOnExit>
                      <Divider />
                      <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ fontSize: 11.5, color: GREY, mb: 1.5 }}>
                          Attributes of <strong>{factSheet.name} → {row.name}</strong>
                        </Typography>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                          gap: 1.5,
                        }}>
                          {RELATION_ATTRIBUTES.map((attr) => (
                            <Box key={attr.key} sx={attr.full ? { gridColumn: '1 / -1' } : undefined}>
                              <AttributeEditor
                                attr={attr}
                                value={row.attributes[attr.key]}
                                onChange={(v) => setAttribute(def.key, row.id, attr.key, v)}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })
            )}

            <Autocomplete
              size="small"
              options={choices}
              value={null}
              blurOnSelect
              clearOnBlur
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => addTarget(def, v)}
              noOptionsText={`No ${typeMeta(def.target).label} fact sheets available in this workspace`}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TypeBadge type={o.type} size={16} />
                    <span style={{ fontSize: 13 }}>{o.name}</span>
                    {o.fields?.lifecycleState && (
                      <span style={{ fontSize: 11, color: GREY }}>{o.fields.lifecycleState}</span>
                    )}
                  </Stack>
                </li>
              )}
              sx={{ maxWidth: 420 }}
              renderInput={(params) => (
                <TextField {...params}
                  label={def.single && rows.length ? `Replace ${def.label.toLowerCase()}` : `Add ${typeMeta(def.target).label}`}
                  placeholder="Search fact sheets…" />
              )}
            />
          </Box>
        );
      })}
    </Paper>
  );
}
